import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {AsyncPipe, NgIf} from "@angular/common";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {PopupService} from '../../../../../services/popup.service';
import {Observable, Subscription} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../../state/app.state";
import {updateRevenue, updateRevenueSuccess, updateRevenueFailure, updateReport} from "../../../../../state/search/search.actions";
import {Actions, ofType} from "@ngrx/effects";
import {selectRevenueUpdateLoading, selectReport} from "../../../../../state/search/search.selectors";
import {PromptService} from "../../../../../services/prompt.service";
import {selectCompany} from "../../../../../state/report/report.selectors";
import {updateCompany} from "../../../../../state/report/report.actions";
import {take} from "rxjs/operators";

@Component({
  selector: 'app-edit-revenue',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    AsyncPipe
  ],
  templateUrl: './edit-revenue.component.html',
  styleUrl: './edit-revenue.component.scss'
})
export class EditRevenueComponent implements OnChanges, OnDestroy {

  @Input() isVisible: boolean = false;
  @Input() shortDescription: string = '';
  @Input() longDescription: string = '';
  @Input() searchId: number;
  @Input() searchCompanyId: number;
  @Input() reportId: number;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();

  form: FormGroup;
  loading$: Observable<boolean>;

  private subscriptions = new Subscription();

  constructor(
    public formBuilder: FormBuilder,
    private popupService: PopupService,
    private store: Store<AppState>,
    private actions$: Actions,
    private promptService: PromptService
  ) {
    this.form = formBuilder.group(
      {
        shortDescription: ['', [Validators.required]],
        longDescription: ['']
      },
      { updateOn: "blur" }
    );

    this.loading$ = this.store.select(selectRevenueUpdateLoading);

    // Subscribe to success action
    this.subscriptions.add(
      this.actions$.pipe(ofType(updateRevenueSuccess)).subscribe((action) => {
        console.log(`💰 [EditRevenue] Update successful`, { reportId: this.reportId });

        // Get the updated form values
        const updatedShort = this.form.value.shortDescription;
        const updatedLong = this.form.value.longDescription || '';

        // Create the updated revenue data structure
        const updatedRevenueData = {
          short: updatedShort,
          long: updatedLong
        };

        // Create the updated insight object
        const updatedInsight = {
          response: updatedRevenueData,
          status: 'finished',
          generated: true,
          updated_at: new Date().toISOString()
        };

        // Update ONLY the report store
        // The report component will automatically sync the company from the report
        this.store.select(selectReport).pipe(take(1)).subscribe(reportResponse => {
          if (reportResponse?.report) {
            const currentReport = reportResponse.report;

            // Update the company insights in the search_companies array
            const updatedSearchCompanies = currentReport.search_companies?.map((company: any) => {
              if (company.id === currentReport.search_company_id) {
                return {
                  ...company,
                  insights: {
                    ...company.insights,
                    company_revenue: {
                      ...company.insights?.company_revenue,
                      ...updatedInsight
                    }
                  }
                };
              }
              return company;
            }) || currentReport.search_companies;

            const updatedReport = {
              ...currentReport,
              search_companies: updatedSearchCompanies,
              insights: {
                ...currentReport.insights,
                company_revenue: {
                  ...currentReport.insights?.company_revenue,
                  ...updatedInsight
                }
              }
            };

            this.store.dispatch(updateReport({ report: updatedReport }));
            console.log(`📊 [EditRevenue] Report store updated (company will sync automatically)`, { reportId: this.reportId });

            // Notify after store update - the report component will handle company sync
            setTimeout(() => {
              // Send the full insight structure (with response wrapper) to match what components expect
              this.promptService.notifyPromptUpdate('ASK_ASSISTANT_COMPANY_REVENUE', updatedInsight, this.reportId);
              console.log(`📢 [EditRevenue] Prompt update notification sent`, { reportId: this.reportId });
            }, 50);
          }
        });

        this.popupService.notify('Revenue has been updated successfully!', 'Update success', 'success');
        this.closeModal();
      })
    );

    // Subscribe to failure action
    this.subscriptions.add(
      this.actions$.pipe(ofType(updateRevenueFailure)).subscribe((error) => {
        console.error('Revenue update failed:', error);
        this.popupService.notify('Failed to update revenue', 'Update failed', 'error');
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // When the modal opens, populate form with incoming data
    if (changes['isVisible'] && this.isVisible) {
      this.form.patchValue({
        shortDescription: this.shortDescription || '',
        longDescription: this.longDescription || ''
      });
    }
  }

  closeModal() {
    this.isVisible = false;
    this.modalClosed.emit();
    this.form.reset();
  }

  saveRevenue() {
    if(this.form.valid) {
      const shortDescription = this.form.value.shortDescription;
      const longDescription = this.form.value.longDescription || '';

      // Dispatch the updateRevenue action
      this.store.dispatch(updateRevenue({
        searchId: this.searchId,
        searchCompanyId: this.searchCompanyId,
        revenue: shortDescription,
        longDescription: longDescription
      }));
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
